if (!customElements.get('atrophy-product')) {
  customElements.define('atrophy-product', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      const gallery = this.querySelector('.gs-product-page__gallery');
      const thumbnails = [...this.querySelectorAll('[data-gs-thumbnail-target]')];
      const items = [...(gallery?.querySelectorAll('[data-media-id]') || [])];
      const mobile = matchMedia('(max-width: 999px)');
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const behavior = () => reducedMotion.matches ? 'auto' : 'smooth';
      let activeIndex = 0;

      const activate = index => {
        activeIndex = index;
        thumbnails.forEach((thumbnail, i) => thumbnail.setAttribute('aria-current', String(i === index)));
        items.forEach((item, i) => {
          if (i !== index && mobile.matches) {
            item.querySelectorAll('video').forEach(video => video.pause());
            item.querySelectorAll('video-media, model-media').forEach(media => media.pause?.());
          }
        });
      };
      const select = (index, motion = behavior()) => {
        const item = items[index];
        if (!gallery || !item || !mobile.matches) return;
        activate(index);
        const left = gallery.scrollLeft + item.getBoundingClientRect().left - gallery.getBoundingClientRect().left;
        gallery.scrollTo({ left, behavior: motion });
        const thumbnail = thumbnails[index];
        if (thumbnail) {
          const rail = thumbnail.parentElement;
          const thumbnailLeft = rail.scrollLeft + thumbnail.getBoundingClientRect().left - rail.getBoundingClientRect().left;
          rail.scrollTo({ left: thumbnailLeft - (rail.clientWidth - thumbnail.clientWidth) / 2, behavior: motion });
        }
      };
      thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', () => select(index), { signal }));
      gallery?.addEventListener('keydown', event => {
        if (!mobile.matches || event.target !== gallery || items.length < 2) return;
        const indexes = { ArrowRight: Math.min(activeIndex + 1, items.length - 1), ArrowLeft: Math.max(activeIndex - 1, 0), Home: 0, End: items.length - 1 };
        if (event.key in indexes) {
          event.preventDefault();
          select(indexes[event.key]);
        }
      }, { signal });
      gallery?.addEventListener('scroll', () => {
        if (!mobile.matches || !items.length) return;
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const left = gallery.getBoundingClientRect().left;
          const distances = items.map(item => Math.abs(item.getBoundingClientRect().left - left));
          activate(distances.indexOf(Math.min(...distances)));
        });
      }, { passive: true, signal });
      this.resizeObserver?.disconnect();
      if (gallery) {
        this.resizeObserver = new ResizeObserver(() => select(activeIndex, 'instant'));
        this.resizeObserver.observe(gallery);
      }
      this.querySelectorAll('[data-gs-reviews-link]').forEach(link => link.addEventListener('click', () => {
        const target = document.getElementById(link.hash.slice(1));
        const disclosure = target?.querySelector('details');
        if (disclosure) disclosure.open = true;
      }, { signal }));

      const form = this.querySelector('[data-gs-product-form]');
      const input = this.querySelector('[data-gs-variant-id]');
      const variantData = this.querySelector('[data-gs-variants]');
      const configData = this.querySelector('[data-gs-product-config]');
      if (!form || !input || !variantData || !configData) return;
      const variants = JSON.parse(variantData.textContent);
      const config = JSON.parse(configData.textContent);
      const button = this.querySelector('[data-gs-add-button]');
      const label = this.querySelector('[data-gs-add-label]');
      const payment = this.querySelector('[data-gs-payment]');
      const hint = this.querySelector('.gs-product-page__availability');
      let selectedVariant = variants.find(variant => String(variant.id) === input.value);
      const showVariant = (variant, notify = false) => {
        const previousVariant = selectedVariant;
        selectedVariant = variant;
        input.value = variant ? String(variant.id) : '';
        input.disabled = !variant;
        button.disabled = !variant?.available;
        label.textContent = !variant ? config.unavailableText : variant.available ? config.addText : config.soldOutText;
        if (payment) payment.hidden = !variant?.available;
        if (hint) hint.hidden = !variant?.available;
        const prices = variant ? config.prices[String(variant.id)] : null;
        this.querySelectorAll('[data-gs-price-current], [data-gs-buy-price], [data-gs-button-price]').forEach(node => {
          node.textContent = prices?.price || '';
        });
        this.querySelectorAll('[data-gs-compare-price], [data-gs-buy-compare-price]').forEach(node => {
          node.hidden = !variant || !(variant.compare_at_price > variant.price);
          node.textContent = node.hidden ? '' : prices.compare;
        });
        if (notify) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
          form.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { formId: form.getAttribute('id'), variant: variant || null, previousVariant: previousVariant || null } }));
          const url = new URL(location.href);
          if (variant) url.searchParams.set('variant', variant.id);
          else url.searchParams.delete('variant');
          history.replaceState(history.state, '', url);
          if (variant?.featured_media) {
            const index = items.findIndex(item => item.dataset.mediaId === String(variant.featured_media.id));
            if (index >= 0) select(index);
          }
        }
      };
      form.addEventListener('change', event => {
        if (!event.target.matches('[data-gs-option]')) return;
        const options = [...form.querySelectorAll('[data-gs-option]:checked')].map(option => option.value);
        const variant = variants.find(candidate => candidate.options.every((value, index) => value === options[index]));
        showVariant(variant, true);
      }, { signal });
      form.addEventListener('submit', event => {
        if (!selectedVariant?.available) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true, signal });
      showVariant(selectedVariant);
      if (new URL(location.href).searchParams.has('variant') && selectedVariant?.featured_media) {
        const index = items.findIndex(item => item.dataset.mediaId === String(selectedVariant.featured_media.id));
        if (index >= 0) select(index, 'instant');
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
    }
  });
}
